import { BasicObservable, Specs } from '@doxyme/extensions-common';
import AppletCapability from '../applet';
import type Instance from '../../../instance';
import { type IframeRuntime } from '../../../runtimes/iframeRuntime';

type Constraints = Specs.OverlayApplet.Constraints;
type Applet = Specs.OverlayApplet.Applet;
type AppletConfig = Specs.OverlayApplet.Config;

const { OverlayApplet } = Specs;

/**
 * Overlay Applet stores the frames registered to any extension using this capability. It only exposes the 'active' frame, which is constantly updated to be the first in the array of registered frames.
 */
export default class OverlayAppletCapability extends AppletCapability<Constraints> {
  static id = OverlayApplet.id;

  protected readonly activeFrame: BasicObservable<Applet | undefined>;

  constructor() {
    super(OverlayApplet);
    this.activeFrame = new BasicObservable<Applet | undefined>(
      this._frames.value[0],
    );
    this._frames.onChange((frames) => {
      this.activeFrame.value = frames[0];
    });
    this.handlers.push(
      [OverlayApplet.Actions.Attach, this.attach.bind(this)],
      [OverlayApplet.Actions.Detach, this.detach.bind(this)],
    );
  }

  /**
   * Get the active frame
   * @description This is an observable that will update whenever the active frame is updated. See {@link BasicObservable} for more information.
   * @returns The active frame if available, as an observable.
   * @example
   * ```ts
   * const activeFrame = modalApplet.getActiveFrame();
   * activeFrame.onChange((frame) => {
   *  console.log('Active frame changed', frame);
   * });
   * ```
   */
  getActiveFrame(): BasicObservable<Applet | undefined> {
    return this.activeFrame;
  }

  protected override getFrameItem(
    instance: Instance,
    runtime: IframeRuntime,
    config: AppletConfig,
  ): Applet {
    return {
      ...super.getFrameItem(instance, runtime, config),
      variant: config.variant,
    };
  }
}
